// =============================================
// src/components/modals/ExpertWorkflowSection.jsx
// =============================================

import React, { useState, useEffect } from "react";
import {
  Check,
  X,
  Database,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  Copy,
  Download,
} from "lucide-react";

// Import your actual tools and constants
import { AVAILABLE_TOOLS, DRUG_NAME_BANK, styles } from "../../utils/constants";

export default function ExpertWorkflowSection({ workflowData: initialData }) {
  const [workflowData, setWorkflowData] = useState(initialData);
  const [stepValidation, setStepValidation] = useState({}); // 'correct', 'incorrect', 'alternative', or undefined
  const [selectedFunctions, setSelectedFunctions] = useState({});
  const [feedbackNotes, setFeedbackNotes] = useState({});
  const [alternativeSuggestions, setAlternativeSuggestions] = useState({}); // For alternative step suggestions
  const [isFullyReviewed, setIsFullyReviewed] = useState(false);
  const [generalFeedback, setGeneralFeedback] = useState("");
  const [showThankYou, setShowThankYou] = useState(false);
  const [targets, setTargets] = useState([]);

  // NEW: simple tabs state
  const [activeTab, setActiveTab] = useState("targets"); // 'targets' | 'drugs'

  // Update workflow data when props change
  useEffect(() => {
    setWorkflowData(initialData);
  }, [initialData]);

  // Initialize validation status when workflow data changes
  useEffect(() => {
    if (workflowData && workflowData.workflow) {
      const initialValidation = {};
      const initialFunctions = {};
      const initialAlternatives = {};
      workflowData.workflow.steps.forEach((step) => {
        initialValidation[step.stepNumber] = undefined; // undefined, 'correct', 'incorrect', or 'alternative'
        // Initialize selected function - try to parse from existing tool or use first available
        const tool = AVAILABLE_TOOLS.find((t) => t.name === step.tool);
        initialFunctions[step.stepNumber] = tool?.functions?.[0] || "";
        // Initialize alternative suggestions structure
        initialAlternatives[step.stepNumber] = {
          tool: "",
          function: "",
          description: "",
          reasoning: "",
        };
      });
      setStepValidation(initialValidation);
      setSelectedFunctions(initialFunctions);
      setAlternativeSuggestions(initialAlternatives);
    }
  }, [workflowData]);

  // Check if all steps are reviewed
  useEffect(() => {
    if (workflowData?.workflow?.steps) {
      const allReviewed = workflowData.workflow.steps.every(
        (step) => stepValidation[step.stepNumber] !== undefined
      );
      setIsFullyReviewed(allReviewed);
    }
  }, [stepValidation, workflowData]);

 // ----------------------------
  // Mock Data for the two tables
  // ----------------------------
  useEffect(() => {
  const MOCK_TARGETS = [
    {
      gene: "CSF1R",
      targetClass: "Receptor tyrosine kinase",
      score: 86,
      lrEdges: [
        { ligand: "CSF1", receptor: "CSF1R", pair: "Astrocytes/Neurons→Microglia", conf: 0.82 },
        { ligand: "IL34", receptor: "CSF1R", pair: "Astrocyte→Microglia", conf: 0.71 },
      ],
      spatialNote:
        "CSF1R inhibition with busulfan enables complete microglial depletion and replacement with engineered donor cells in mouse brains.",
      alzforumLink:
        "https://www.alzforum.org/news/research-news/regional-cues-and-natural-killer-cells-steer-microglial-aging",
      riskFlags: ["Microglial proliferation"],
    },
    {
      gene: "TREM2",
      targetClass: "Immune receptor",
      score: 81,
      lrEdges: [{ ligand: "APOE", receptor: "TREM2", pair: "Astrocyte→Microglia", conf: 0.76 }],
      spatialNote:
        "TREM2 haploinsufficiency reduces microglial plaque responses in AD mice without affecting amyloid load, implicating impaired microglial function.",
      alzforumLink:
        "https://www.alzforum.org/news/research-news/trem2-mystery-altered-microglia-no-effect-plaques",
      riskFlags: ["Variant-specific effects"],
    },
    {
      gene: "TYROBP",
      targetClass: "Adaptor protein",
      score: 78,
      lrEdges: [{ ligand: "TREM2", receptor: "TYROBP", pair: "Microglia signaling", conf: 0.91 }],
      spatialNote:
        "TYROBP loss-of-function mutations disrupt microglial signaling, causing Nasu-Hakola disease with neurodegeneration, demyelination, and gliosis.",
      alzforumLink:
        "https://www.alzforum.org/news/research-news/nasu-hakola-disease-dap12-deficient-microglia-kick-repair-overdrive",
      riskFlags: ["Immune modulation"],
    },
  ];

  // Set initial data
  setTargets(MOCK_TARGETS);

  // Fetch and update each gene's targetClass
  MOCK_TARGETS.forEach((target, index) => {
    fetch(`https://mygene.info/v3/query?q=${target.gene}&species=human&fields=name,type_of_gene`)
      .then(res => res.json())
      .then(data => {
        const geneInfo = data.hits[0];
        if (geneInfo) {
          setTargets(prev => {
            const updated = [...prev];
            // Simple mapping of gene types to readable classes
            updated[index].targetClass = 
              geneInfo.name?.includes('receptor') ? 'Receptor' :
              geneInfo.name?.includes('kinase') ? 'Kinase' :
              geneInfo.type_of_gene === 'protein-coding' ? 'Protein' :
              'Gene product';
            return updated;
          });
        }
      })
      .catch(err => console.error(`Error fetching ${target.gene}:`, err));
  });
}, []);

  if (!workflowData) return null;

  // Handle feedback note
  const handleFeedbackNote = (stepNumber, note) => {
    setFeedbackNotes((prev) => ({ ...prev, [stepNumber]: note }));
  };

  // Handle alternative suggestion updates
  const handleAlternativeSuggestion = (stepNumber, field, value) => {
    setAlternativeSuggestions((prev) => ({
      ...prev,
      [stepNumber]: {
        ...prev[stepNumber],
        [field]: value,
      },
    }));
  };

  // Toggle step validation status
  const setStepStatus = (stepNumber, status) => {
    setStepValidation((prev) => ({
      ...prev,
      [stepNumber]: prev[stepNumber] === status ? undefined : status,
    }));
  };

  // Export feedback
  const exportFeedback = () => {
    const exportData = {
      ...workflowData,
      userFeedback: {
        submittedAt: new Date().toISOString(),
        fullyReviewed: isFullyReviewed,
        generalFeedback: generalFeedback,
        stepFeedback: feedbackNotes,
        alternativeSuggestions: alternativeSuggestions,
        selectedFunctions: selectedFunctions,
        stepValidation: stepValidation, // Contains 'correct', 'incorrect', 'alternative', or undefined for each step
        correctSteps: Object.keys(stepValidation)
          .filter((key) => stepValidation[key] === "correct")
          .map(Number),
        incorrectSteps: Object.keys(stepValidation)
          .filter((key) => stepValidation[key] === "incorrect")
          .map(Number),
        alternativeSteps: Object.keys(stepValidation)
          .filter((key) => stepValidation[key] === "alternative")
          .map(Number),
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `workflow_feedback_${Date.now()}.json`;
    a.click();
  };

  // Copy workflow as template
  const copyAsTemplate = () => {
    const templateData = {
      templateName: `Template_${workflowData.metadata.query.substring(0, 30)}...`,
      query: workflowData.metadata.query,
      steps: workflowData.workflow.steps.map((step) => ({
        tool: step.tool,
        function: selectedFunctions[step.stepNumber],
        description: step.description,
        userFeedback: feedbackNotes[step.stepNumber] || null,
        wasHelpful: stepValidation[step.stepNumber] === "correct",
      })),
      createdBy: "user",
      createdAt: new Date().toISOString(),
    };

    navigator.clipboard.writeText(JSON.stringify(templateData, null, 2));
    alert(
      "Template saved to clipboard! You can share this with others or use it as a starting point for similar queries."
    );
  };



  const MOCK_DRUGS = [
    {
      compound: "Pexidartinib",
      targets: ["CSF1R"],
      phase: "Approved (non-AD)",
      bbb: "Unknown",
      reversalScore: 68,
      safetyFlags: ["Hepatotoxicity"],
      evidence: [
        {
          pmid: "AF-778899",
          title: "Alzforum: CSF1R inhibitors roundup",
          year: 2022,
          snippet: "Brain exposure uncertain",
          source: "Alzforum",
        },
      ],
    },
    {
      compound: "AL002 (TREM2 mAb)",
      targets: ["TREM2"],
      phase: "Phase 2",
      bbb: "Likely",
      reversalScore: 62,
      safetyFlags: ["Immune activation"],
      evidence: [
        { pmid: "37511223", title: "TREM2 agonism modulates microglia", year: 2023, snippet: "Enhances DAM phenotype", source: "PubMed" },
      ],
    },
    {
      compound: "DAP12 modulator",
      targets: ["TYROBP/DAP12"],
      phase: "Preclinical",
      bbb: "Unknown",
      reversalScore: 55,
      safetyFlags: ["Immune response"],
      evidence: [
        { pmid: "39456789", title: "TYROBP/DAP12 pathway modulation", year: 2024, snippet: "Targeting microglial signaling hub", source: "PubMed" },
      ],
    },
  ];

  // ----------------------------
  // Color helpers (Viridis, lightweight)
  // ----------------------------
  const lerp = (a, b, t) => a + (b - a) * t;
  const hexToRgb = (hex) => {
    const n = hex.replace("#", "");
    const s = n.length === 3 ? n.split("").map((x) => x + x).join("") : n;
    const bigint = parseInt(s, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
  };
  const rgbToHex = (r, g, b) =>
    "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
  const lerpColor = (c1, c2, t) => {
    const a = hexToRgb(c1),
      b = hexToRgb(c2);
    return rgbToHex(
      Math.round(lerp(a.r, b.r, t)),
      Math.round(lerp(a.g, b.g, t)),
      Math.round(lerp(a.b, b.b, t))
    );
  };
  const VIRIDIS = [
    { t: 0.0, c: "#440154" },
    { t: 0.2, c: "#48186a" },
    { t: 0.4, c: "#2d708e" },
    { t: 0.6, c: "#22a884" },
    { t: 0.8, c: "#7ad151" },
    { t: 1.0, c: "#fde725" },
  ];
  const viridis = (t) => {
    t = Math.max(0, Math.min(1, t));
    for (let i = 1; i < VIRIDIS.length; i++) {
      const a = VIRIDIS[i - 1],
        b = VIRIDIS[i];
      if (t <= b.t) return lerpColor(a.c, b.c, (t - a.t) / (b.t - a.t));
    }
    return VIRIDIS[VIRIDIS.length - 1].c;
  };
  const toUnit = (z, min = -1, max = 1) =>
    (Math.max(min, Math.min(max, z)) - min) / (max - min);

  // ----------------------------
  // Table helpers
  // ----------------------------
  const thStyle = {
    padding: "12px 16px",
    textAlign: "left",
    color: "#374151",
    fontWeight: 700,
    borderBottom: "1px solid #e5e7eb",
    background: "linear-gradient(to right, #f9fafb, #f3f4f6)",
  };

  const ExpressionPlot = ({ gene }) => {
    const zScores = {
      CSF1R: { none: 0.48, sparse: 0.63, moderate: -0.7, frequent: -0.99 },
      TREM2: { none: -0.25, sparse: -0.73, moderate: 1.0, frequent: -0.73 },
      TYROBP: { none: -0.73, sparse: -0.43, moderate: 1.0, frequent: -0.58 },
    };
    const cats = ["none", "sparse", "moderate", "frequent"];
    return (
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 40 }}>
        {cats.map((cat) => {
          const z = zScores[gene]?.[cat] ?? 0;
          const height = 8 + ((z + 1) / 2) * 28;
          const color = viridis(toUnit(z));
          return (
            <div key={cat} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div
                style={{
                  width: 12,
                  height: `${height}px`,
                  backgroundColor: color,
                  borderRadius: "2px 2px 0 0",
                  opacity: 0.95,
                  boxShadow: `0 2px 4px ${color}33`,
                }}
              />
              <span style={{ fontSize: 7, color: "#6b7280", marginTop: 2 }}>{cat}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // ----------------------------
  // Targets Table
  // ----------------------------
  const TargetsTable = ({ data }) => (
    <div
      style={{
        backgroundColor: "#fff",
        borderRadius: 12,
        border: "2px solid #e2e8f0",
        overflow: "hidden",
        boxShadow:
          "0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          background: "linear-gradient(to right, #2563eb, #7c3aed)",
          padding: "12px 20px",
          color: "white",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Database size={16} />
          <h3
            style={{
              fontSize: 16,
              fontWeight: 700,
              margin: 0,
              letterSpacing: 0.2,
            }}
          >
            Ranked Targets
          </h3>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            fontSize: 14,
            borderCollapse: "collapse",
            background: "linear-gradient(to bottom, #ffffff, #fafafa)",
          }}
        >
          <thead>
            <tr style={{ color: "#374151" }}>
              <th style={thStyle}>Gene</th>
              <th style={{ ...thStyle, width: 160 }}>Class</th>
              <th style={{ ...thStyle, width: 96 }}>Composite</th>
              <th style={thStyle}>Expression</th>
              <th style={thStyle}>L-R Edges</th>
              <th style={{ ...thStyle, minWidth: 320 }}>Alzforum Summary</th>
              <th style={thStyle}>Phenotype</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.gene} style={{ borderBottom: "1px solid #eef2f7" }}>
                <td style={{ padding: 16, fontWeight: 700, color: "#1e40af" }}>{row.gene}</td>
                <td style={{ padding: 16, color: "#6b7280" }}>{row.targetClass}</td>
                <td style={{ padding: 16 }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      background: "linear-gradient(to right, #10b981, #3b82f6)",
                      color: "white",
                      borderRadius: 9999,
                      fontSize: 12,
                      fontWeight: 700,
                      boxShadow: "0 2px 6px rgba(59,130,246,0.25)",
                    }}
                  >
                    {row.score}
                  </span>
                </td>
                <td style={{ padding: 16 }}>
                  <ExpressionPlot gene={row.gene} />
                </td>
                <td style={{ padding: 16 }}>
                  {row.lrEdges?.length ? (
                    row.lrEdges.map((e, idx) => (
                      <div
                        key={idx}
                        style={{
                          fontSize: 12,
                          background: "#faf5ff",
                          borderRadius: 6,
                          padding: "6px 8px",
                          marginBottom: 6,
                          border: "1px solid #ede9fe",
                        }}
                      >
                        <div style={{ fontWeight: 700, color: "#6d28d9" }}>
                          {e.ligand}→{e.receptor}
                        </div>
                        <div style={{ fontSize: 10, color: "#7c3aed" }}>
                          ({e.pair}, {Math.round(e.conf * 100)}%)
                        </div>
                      </div>
                    ))
                  ) : (
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>—</span>
                  )}
                </td>
                <td style={{ padding: 16 }}>
                  <div
                    style={{
                      background: "#fffbeb",
                      padding: 12,
                      borderRadius: 8,
                      border: "1px solid #fde68a",
                    }}
                  >
                    <p style={{ color: "#374151", fontSize: 12, margin: 0, lineHeight: 1.5 }}>
                      {row.spatialNote}
                    </p>
                    {row.alzforumLink && (
                      <a
                        href={row.alzforumLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#2563eb",
                          textDecoration: "underline",
                          fontSize: 10,
                          fontWeight: 600,
                          display: "inline-block",
                          marginTop: 8,
                        }}
                      >
                        View on Alzforum →
                      </a>
                    )}
                  </div>
                </td>
                <td style={{ padding: 16 }}>
                  {/* Outline chips with small status dot */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {row.riskFlags?.map((f) => (
                      <span
                        key={f}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "4px 10px",
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          lineHeight: 1,
                          color: "#4c1d95", // purple-900-ish
                          background: "#f5f3ff", // purple-50
                          border: "1px solid #ddd6fe", // purple-200
                        }}
                      >
                        
                        {f}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ----------------------------
  // Drugs Table
  // ----------------------------
  const DrugsTable = ({ data }) => (
    <div
      style={{
        backgroundColor: "#fff",
        borderRadius: 12,
        border: "2px solid #e2e8f0",
        overflow: "hidden",
        boxShadow:
          "0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          background: "linear-gradient(to right, #7c3aed, #db2777)",
          padding: "12px 20px",
          color: "white",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Database size={16} />
          <h3
            style={{
              fontSize: 16,
              fontWeight: 700,
              margin: 0,
              letterSpacing: 0.2,
            }}
          >
            Drug Candidates
          </h3>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            fontSize: 14,
            borderCollapse: "collapse",
            background: "linear-gradient(to bottom, #ffffff, #fafafa)",
          }}
        >
          <thead>
            <tr style={{ color: "#374151" }}>
              <th style={thStyle}>Compound</th>
              <th style={thStyle}>Targets</th>
              <th style={{ ...thStyle, width: 160 }}>Phase</th>
              <th style={{ ...thStyle, width: 160 }}>BBB Penetration</th>
              <th style={{ ...thStyle, width: 200 }}>EHR/Survival</th>
              <th style={thStyle}>Safety Flags</th>
              <th style={thStyle}>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.compound} style={{ borderBottom: "1px solid #eef2f7" }}>
                <td style={{ padding: 16, fontWeight: 700, color: "#6b21a8" }}>{row.compound}</td>
                <td style={{ padding: 16 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {row.targets.map((t) => (
                      <span
                        key={t}
                        style={{
                          padding: "2px 10px",
                          borderRadius: 9999,
                          background: "#f3e8ff",
                          color: "#6b21a8",
                          fontSize: 12,
                          border: "1px solid #e9d5ff",
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </td>
                <td style={{ padding: 16 }}>
                  <span
                    style={{
                      padding: "4px 10px",
                      borderRadius: 9999,
                      fontSize: 12,
                      color: "#fff",
                      background:
                        row.phase.includes("Approved")
                          ? "linear-gradient(to right, #10b981, #059669)"
                          : row.phase.includes("Phase")
                          ? "linear-gradient(to right, #3b82f6, #06b6d4)"
                          : "#9ca3af",
                    }}
                  >
                    {row.phase}
                  </span>
                </td>
                <td style={{ padding: 16 }}>
                  <span
                    style={{
                      padding: "2px 10px",
                      borderRadius: 9999,
                      fontSize: 12,
                      border: "1px solid #e5e7eb",
                      background: row.bbb === "Likely" ? "#ecfdf5" : "#f9fafb",
                      color: row.bbb === "Likely" ? "#065f46" : "#6b7280",
                    }}
                  >
                    {row.bbb}
                  </span>
                </td>
                <td style={{ padding: 16 }}>
                  {row.compound === "Pexidartinib" ? (
                    <div
                      style={{
                        background: "linear-gradient(to right, #ecfdf5, #eff6ff)",
                        border: "1px solid #d1fae5",
                        borderRadius: 8,
                        padding: 8,
                      }}
                    >
                      <div style={{ fontSize: 12, color: "#047857", fontWeight: 700 }}>
                        EHR Data Available
                      </div>
                      <div style={{ fontSize: 10, color: "#065f46" }}>
                        HR: 0.72 (95% CI: 0.61–0.85)
                      </div>
                      <div style={{ fontSize: 10, color: "#6b7280" }}>n=1,247 patients</div>
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: 8,
                        fontSize: 12,
                        color: "#9ca3af",
                        textAlign: "center",
                      }}
                    >
                      No EHR data available
                    </div>
                  )}
                </td>
                <td style={{ padding: 16 }}>
                  {/* Outline chips with small status dot (safety) */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {row.safetyFlags.map((f) => (
                      <span
                        key={f}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "4px 10px",
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          lineHeight: 1,
                          color: "#7f1d1d", // red-900-ish
                          background: "#fff1f2", // rose-50
                          border: "1px solid #fecdd3", // rose-200
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: "#ef4444", // red-500 dot
                            flex: "0 0 auto",
                          }}
                        />
                        {f}
                      </span>
                    ))}
                  </div>
                </td>
                <td style={{ padding: 16 }}>
                  <div style={{ display: "grid", gap: 6 }}>
                    {row.evidence.map((ev, idx) => (
                      <div
                        key={idx}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 8,
                          padding: 8,
                          background: "linear-gradient(to right, #faf5ff, #fdf2f8)",
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 12 }}>{ev.title}</div>
                        <div style={{ fontSize: 10, color: "#6b7280" }}>
                          {ev.source} • {ev.year} • PMID: {ev.pmid}
                        </div>
                        <div style={{ fontSize: 12, color: "#374151", marginTop: 4 }}>
                          {ev.snippet}
                        </div>
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ----------------------------
  // Render
  // ----------------------------
  return (
    <>
      {/* Tabs Header */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          marginTop: 100,
          background: "linear-gradient(to right, #f8fafc, #f3f4f6)",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: 6,
          width: "fit-content",
        }}
      >
        <button
          onClick={() => setActiveTab("targets")}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 13,
            color: activeTab === "targets" ? "#fff" : "#334155",
            background:
              activeTab === "targets"
                ? "linear-gradient(to right, #2563eb, #7c3aed)"
                : "transparent",
            boxShadow: activeTab === "targets" ? "0 2px 8px rgba(99,102,241,0.35)" : "none",
            transition: "all 0.15s ease",
          }}
        >
          Targets
        </button>
        <button
          onClick={() => setActiveTab("drugs")}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 13,
            color: activeTab === "drugs" ? "#fff" : "#334155",
            background:
              activeTab === "drugs"
                ? "linear-gradient(to right, #7c3aed, #db2777)"
                : "transparent",
            boxShadow: activeTab === "drugs" ? "0 2px 8px rgba(236,72,153,0.35)" : "none",
            transition: "all 0.15s ease",
          }}
        >
          Drugs
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "targets" ? (
        <TargetsTable data={targets} />
      ) : (
        <DrugsTable data={MOCK_DRUGS} />
      )}

      {/* Feedback Panel */}
      <div
        style={{
          marginTop: 100,
          padding: 28,
          backgroundColor: "#fff",
          borderRadius: 16,
          border: "2px solid #e2e8f0",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            marginBottom: 24,
            paddingBottom: 20,
            borderBottom: "2px solid #f1f5f9",
          }}
        >
          <div>
            <h3
              style={{
                margin: "0 0 8px 0",
                color: "#0f172a",
                fontSize: 24,
                fontWeight: 700,
                background: "linear-gradient(135deg, #2b3c75ff 0%, #062a5fff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              User Workflow Feedback
            </h3>
            <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>
              Please take a moment to give feedback; it improves future workflows.
            </p>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              style={{
                padding: "10px 24px",
                backgroundColor: isFullyReviewed ? "#10b981" : "#94a3b8",
                color: "white",
                border: "none",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: isFullyReviewed ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                gap: 8,
                opacity: isFullyReviewed ? 1 : 0.7,
                transition: "all 0.2s ease",
              }}
              onClick={() => setShowThankYou(true)}
              disabled={!isFullyReviewed}
              title={isFullyReviewed ? "Submit your feedback" : "Please review all steps first"}
            >
              {isFullyReviewed ? "Submit Feedback" : "Submit feedback after reviewing all steps"}
            </button>
          </div>
        </div>

        {/* Status Bar */}
        <div
          style={{
            display: "flex",
            gap: 24,
            padding: 16,
            backgroundColor: "#f8fafc",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            marginBottom: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>Workflow Steps</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 13, color: "#10b981", display: "flex", alignItems: "center", gap: 4 }}>
              <CheckCircle size={14} />
              Correct: {Object.values(stepValidation).filter((v) => v === "correct").length}
            </span>
            <span style={{ fontSize: 13, color: "#f59e0b", display: "flex", alignItems: "center", gap: 4 }}>
              <X size={14} />
              Incorrect: {Object.values(stepValidation).filter((v) => v === "incorrect").length}
            </span>
            <span style={{ fontSize: 13, color: "#f59e0b", display: "flex", alignItems: "center", gap: 4 }}>
              <AlertCircle size={14} />
              Alternative: {Object.values(stepValidation).filter((v) => v === "alternative").length}
            </span>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              Not Reviewed: {Object.values(stepValidation).filter((v) => v === undefined).length}
            </span>
          </div>
          {isFullyReviewed && !showThankYou && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
              <CheckCircle size={16} color="#10b981" />
              <span style={{ fontSize: 13, color: "#10b981", fontWeight: 600 }}>Ready to submit</span>
            </div>
          )}
          {showThankYou && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
              <CheckCircle size={16} color="#10b981" />
              <span style={{ fontSize: 13, color: "#10b981", fontWeight: 600 }}>
                Thank you for your feedback!
              </span>
            </div>
          )}
        </div>

        {/* Workflow Steps Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(450px, calc(100vw - 60px)), 1fr))",
            gap: 16,
            marginBottom: 24,
          }}
        >
          {workflowData?.workflow?.steps?.map((step) => {
            const validationStatus = stepValidation[step.stepNumber];
            const borderColor =
              validationStatus === "correct"
                ? "#10b981"
                : validationStatus === "incorrect"
                ? "#f59e0b"
                : validationStatus === "alternative"
                ? "#f59e0b"
                : "#e2e8f0";
            const backgroundColor =
              validationStatus === "correct"
                ? "#f0fdf4"
                : validationStatus === "incorrect"
                ? "rgba(254, 243, 199, 0.3)"
                : validationStatus === "alternative"
                ? "rgba(254, 243, 199, 0.5)"
                : "#fff";

            return (
              <div
                key={step.stepNumber}
                style={{
                  border: `2px solid ${borderColor}`,
                  borderRadius: 10,
                  padding: 16,
                  backgroundColor: backgroundColor,
                  transition: "all 0.2s ease",
                }}
              >
                {/* Step Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 12,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: "#dde6ebff",
                        color: "black",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {step.stepNumber}
                    </span>

                    {/* Tool & Function Display - Static, not dropdown */}
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: 6,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#0f172a",
                        }}
                      >
                        <Database size={14} />
                        <div style={{ flex: 1, textAlign: "left" }}>
                          <div>{step.tool}</div>
                          {selectedFunctions[step.stepNumber] && (
                            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 400 }}>
                              → {selectedFunctions[step.stepNumber]}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Validation Buttons */}
                <div style={{ display: "flex", gap: 8, marginBottom: 12, paddingLeft: 40 }}>
                  <button
                    style={{
                      padding: "6px 12px",
                      backgroundColor:
                        validationStatus === "correct" ? "#10b981" : "transparent",
                      color: validationStatus === "correct" ? "white" : "#10b981",
                      border: validationStatus === "correct" ? "none" : "1px solid #10b981",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => setStepStatus(step.stepNumber, "correct")}
                    title="This step is correct and helpful"
                  >
                    <CheckCircle size={12} /> Correct
                  </button>
                  <button
                    style={{
                      padding: "6px 12px",
                      backgroundColor:
                        validationStatus === "incorrect" ? "#f59e0b" : "transparent",
                      color: validationStatus === "incorrect" ? "white" : "#f59e0b",
                      border: validationStatus === "incorrect" ? "none" : "1px solid #f59e0b",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => setStepStatus(step.stepNumber, "incorrect")}
                    title="This step needs improvement"
                  >
                    <X size={12} /> Incorrect
                  </button>
                  <button
                    style={{
                      padding: "6px 12px",
                      backgroundColor:
                        validationStatus === "alternative" ? "#f59e0b" : "transparent",
                      color: validationStatus === "alternative" ? "white" : "#f59e0b",
                      border: validationStatus === "alternative" ? "none" : "1px solid #f59e0b",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => setStepStatus(step.stepNumber, "alternative")}
                    title="Suggest a completely different approach"
                  >
                    <AlertCircle size={12} /> Incorrect - Suggest Alternative
                  </button>
                </div>

                {/* Feedback Note for Incorrect */}
                {validationStatus === "incorrect" && (
                  <div style={{ marginLeft: 40, marginBottom: 12 }}>
                    <label
                      style={{
                        fontSize: 11,
                        color: "#f59e0b",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      How could this step be improved?
                    </label>
                    <textarea
                      value={feedbackNotes[step.stepNumber] || ""}
                      onChange={(e) => handleFeedbackNote(step.stepNumber, e.target.value)}
                      placeholder="Please share what would make this step better..."
                      style={{
                        width: "100%",
                        minHeight: 50,
                        padding: 8,
                        marginTop: 4,
                        border: "1px solid #fbbf24",
                        borderRadius: 4,
                        fontSize: 12,
                        color: "#0f172a",
                        resize: "vertical",
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>
                )}

                {/* Alternative Suggestion Form for 'alternative' status */}
                {validationStatus === "alternative" && (
                  <div
                    style={{
                      marginLeft: 40,
                      padding: 16,
                      backgroundColor: "#fff",
                      border: "2px dashed #f59e0b",
                      borderRadius: 8,
                      marginTop: 12,
                    }}
                  >
                    <h5
                      style={{
                        fontSize: 13,
                        color: "#f59e0b",
                        fontWeight: 600,
                        marginTop: 0,
                        marginBottom: 12,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Suggest Alternative Step
                    </h5>

                    {/* Alternative Tool Selection */}
                    <div style={{ marginBottom: 12 }}>
                      <label
                        style={{
                          fontSize: 12,
                          color: "#64748b",
                          fontWeight: 600,
                          display: "block",
                          marginBottom: 4,
                        }}
                      >
                        Alternative Tool & Function:
                      </label>
                      <select
                        value={`${alternativeSuggestions[step.stepNumber]?.tool || ""}|${
                          alternativeSuggestions[step.stepNumber]?.function || ""
                        }`}
                        onChange={(e) => {
                          const [tool, func] = e.target.value.split("|");
                          handleAlternativeSuggestion(step.stepNumber, "tool", tool);
                          handleAlternativeSuggestion(step.stepNumber, "function", func);
                        }}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #e2e8f0",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#0f172a",
                          backgroundColor: "#fff",
                        }}
                      >
                        <option value="|">Select tool and function...</option>
                        {AVAILABLE_TOOLS.map((tool) => (
                          <optgroup key={tool.name} label={tool.name}>
                            {tool.functions.map((func) => (
                              <option key={`${tool.name}|${func}`} value={`${tool.name}|${func}`}>
                                {tool.name} → {func}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>

                    {/* Reasoning */}
                    <div>
                      <label
                        style={{
                          fontSize: 12,
                          color: "#64748b",
                          fontWeight: 600,
                          display: "block",
                          marginBottom: 4,
                        }}
                      >
                        Why is this alternative better?
                      </label>
                      <textarea
                        value={alternativeSuggestions[step.stepNumber]?.reasoning || ""}
                        onChange={(e) =>
                          handleAlternativeSuggestion(step.stepNumber, "reasoning", e.target.value)
                        }
                        placeholder="Explain why this approach would be more effective..."
                        style={{
                          width: "100%",
                          minHeight: 60,
                          padding: 8,
                          border: "1px solid #e2e8f0",
                          borderRadius: 4,
                          fontSize: 12,
                          color: "#0f172a",
                          resize: "vertical",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* General Feedback */}
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", marginBottom: 8 }}>
            Overall Comments & Suggestions
          </h4>
          <textarea
            value={generalFeedback}
            onChange={(e) => setGeneralFeedback(e.target.value)}
            placeholder="Share any general thoughts about this workflow or ideas for making our AI more helpful..."
            style={{
              width: "100%",
              minHeight: 80,
              padding: 12,
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              fontSize: 14,
              color: "#0f172a",
              resize: "vertical",
              backgroundColor: "#f8fafc",
            }}
          />
          <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 8, fontStyle: "italic" }}>
            Your feedback helps us improve AlzTxPILOT's workflow generation. Thank you for helping us learn!
          </p>
        </div>
      </div>
    </>
  );
}
